import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks/typedHooks';
import { apiEditGeneratedContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import {
  EditGeneratedContentRequest,
  EditGeneratedContentResponse
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';

type UseEditGeneratedContentParams = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useEditGeneratedContent = (params?: UseEditGeneratedContentParams) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);

  const mutation = useMutation({
    mutationFn: async (request: Omit<EditGeneratedContentRequest, 'beaconClientId'>) => {
      if (!beaconClientId) {
        throw new Error('Beacon Client ID is required');
      }

      const fullRequest: EditGeneratedContentRequest = {
        ...request,
        beaconClientId
      };

      return await apiEditGeneratedContent(fullRequest);
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: ['LIBRARY_REVIEW_COMPARE'] });
      await queryClient.cancelQueries({ queryKey: ['PROJECT_REVIEW_COMPARE'] });
    },
    onSuccess: (data, variables, context) => {
      showSnackbar({
        message: 'Content updated successfully.',
        type: 'success'
      });

      // Don't invalidate queries here - we're doing optimistic updates
      // The backend will trigger a WebSocket update which will naturally refetch
      // Invalidating immediately causes the refetch to return stale data
      
      params?.onSuccess?.();
    },
    onError: (error: Error, variables, context) => {
      // Rollback is handled in EditableContentCard by invalidating the query
      showSnackbar({
        message: error.message || 'Failed to update content.',
        type: 'error'
      });

      params?.onError?.(error);
    }
  });

  return {
    editGeneratedContent: mutation.mutate,
    editGeneratedContentAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

